import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, fmtDate, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Music, GraduationCap } from "lucide-react";

const TERM_CLASSES = ["CP1", "CP2", "CE1", "CE2", "CM1", "CM2", "7ème Année Fondamentale",
  "8ème Année Fondamentale", "9ème Année Fondamentale", "NS1", "NS2", "NS3", "NS4"];
const ENTRANCE_CLASSES = ["CP1", "CP2", "CE1", "CE2", "CM1", "CM2", "7ème Année Fondamentale",
  "8ème Année Fondamentale", "9ème Année Fondamentale", "NS1", "NS2", "NS3"];
const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];

export default function ExamBank() {
  const { L } = useLang();
  const { data: files, loading, reload } = useFetch(() => api.get("/exam-files"));
  const [tab, setTab] = useState("term"); // term | entrance
  const EMPTY = { grade_level: "", term: "Term 1", subject: "", exam_date: "", file: null };
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [fileKey, setFileKey] = useState(0);

  const upload = async () => {
    if (!f.grade_level || !f.file) { toast.error(L("Select a class and a file", "Choisissez une classe et un fichier")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", tab);
      fd.append("grade_level", f.grade_level);
      if (tab === "term") { fd.append("term", f.term); if (f.exam_date) fd.append("exam_date", new Date(f.exam_date).toISOString()); }
      if (f.subject) fd.append("subject", f.subject);
      fd.append("file", f.file);
      await api.raw.post("/exam-files", fd);
      toast.success(L("Exam uploaded", "Épreuve téléversée"));
      setF({ ...EMPTY, term: f.term }); setFileKey((k) => k + 1); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const dl = async (item) => {
    try { const r = await api.raw.get(`/files/${item.storage_path}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = item.original_filename; a.click(); URL.revokeObjectURL(u); }
    catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  const del = async (item) => { if (!window.confirm(L(`Are you sure you want to delete ${item.original_filename}?`, `Êtes-vous sûr de vouloir supprimer ${item.original_filename} ?`))) return; try { await api.del(`/exam-files/${item.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const classes = tab === "term" ? TERM_CLASSES : ENTRANCE_CLASSES;
  const rows = (files || []).filter((x) => x.kind === tab);

  return (
    <div className="animate-fade-up">
      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("term")} data-testid="exambank-tab-term"
          className={`px-4 py-2 rounded-md text-sm font-medium border ${tab === "term" ? "bg-primary text-primary-foreground border-primary" : "border-input"}`}>{L("Term Exams", "Épreuves par trimestre")}</button>
        <button onClick={() => setTab("entrance")} data-testid="exambank-tab-entrance"
          className={`px-4 py-2 rounded-md text-sm font-medium border ${tab === "entrance" ? "bg-primary text-primary-foreground border-primary" : "border-input"}`}>{L("Entrance Exam", "Examen d'entrée")}</button>
      </div>

      <div className="bg-card border border-border rounded-lg p-5 mb-6">
        <p className="font-heading font-bold mb-3 flex items-center gap-2"><Upload className="h-4 w-4" />{tab === "term" ? L("Upload a term exam (PDF, DOC, MP3, MP4)", "Téléverser une épreuve (PDF, DOC, MP3, MP4)") : L("Upload an entrance exam", "Téléverser un examen d'entrée")}</p>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          {tab === "term" && (
            <Field label={L("Term", "Trimestre")}>
              <Select value={f.term} onValueChange={(v) => setF({ ...f, term: v })}>
                <SelectTrigger data-testid="exambank-term-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {tab === "term" && (
            <Field label={L("Exam date/time (teachers can view only after)", "Date/heure de l'examen (visible aux enseignants après)")}>
              <Input type="datetime-local" value={f.exam_date} onChange={(e) => setF({ ...f, exam_date: e.target.value })} data-testid="exambank-exam-date" />
            </Field>
          )}
          <Field label={L("Class", "Classe")}>
            <Select value={f.grade_level} onValueChange={(v) => setF({ ...f, grade_level: v })}>
              <SelectTrigger data-testid="exambank-class-select"><SelectValue placeholder={L("Select class", "Choisir la classe")} /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={L("Subject (e.g. Language Arts)", "Matière (ex. Langue/Communication)")}>
            <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder={L("Optional", "Optionnel")} data-testid="exambank-subject-input" />
          </Field>
          <Field label={L("File (PDF, DOC, MP3, MP4)", "Fichier (PDF, DOC, MP3, MP4)")}>
            <Input key={fileKey} type="file" accept=".pdf,.doc,.docx,.mp3,.mp4" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="exambank-file-input" />
          </Field>
        </div>
        <Button className="mt-3" onClick={upload} disabled={busy} data-testid="exambank-upload-btn"><Upload className="h-4 w-4 mr-1" />{busy ? L("Uploading…", "Téléversement…") : L("Upload", "Téléverser")}</Button>
      </div>

      {loading ? <Empty title="Loading…" /> : rows.length === 0 ? <Empty title={L("No files yet", "Aucun fichier")} hint={L("Upload exam papers and language audio here.", "Téléversez ici les épreuves et les audios de langue.")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="exambank-list">
          {rows.map((item) => (
            <div key={item.id} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3" data-testid={`exam-file-${item.id}`}>
              <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${item.is_audio ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                {item.is_audio ? <Music className="h-5 w-5" /> : (item.kind === "entrance" ? <GraduationCap className="h-5 w-5" /> : <FileText className="h-5 w-5" />)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{item.original_filename}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="secondary" className="font-normal">{item.grade_level}</Badge>
                  {item.term && <Badge variant="outline" className="font-normal">{item.term}</Badge>}
                  {item.subject && <Badge variant="outline" className="font-normal">{item.subject}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{fmtDate(item.created_at)} · {item.uploaded_by}</p>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={() => dl(item)} data-testid={`exam-dl-${item.id}`}><Download className="h-4 w-4 mr-1" />{L("Download", "Télécharger")}</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(item)} data-testid={`exam-del-${item.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
